import { PencilIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import Editor from "@via/components/rich-text-editor/rte-editor.tsx";
import { ERROR, EditorState } from "@via/utils/constants.tsx";
import { createMarkup } from "@via/utils/html-utils.tsx";
import React, { useState } from "react";
import { Controller, type Control } from "react-hook-form";

interface RichTextInputProps {
  label?: string;
  data?: string;
  onSave?: (data?: string) => void;
  isLoading?: boolean;
  isSubmitLoading?: boolean;
  isError?: boolean;
  control?: Control<any>;
  rules?: Record<string, unknown>;
  name?: string;
  disabled?: boolean;
}

const RichTextInput: React.FC<RichTextInputProps> = ({
  label,
  data = "",
  onSave,
  isLoading,
  isSubmitLoading,
  isError,
  control,
  rules,
  name,
  disabled,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const isControlled = !!control && !!name;

  const updateEditingState = (
    newEditState: EditorState,
    editorData?: string
  ): void => {
    switch (newEditState) {
      case EditorState.Editing:
        setIsEditing(true);
        break;
      case EditorState.Canceled:
        setIsEditing(false);
        break;
      case EditorState.Saved:
        if (onSave) {
          onSave(editorData);
        }
        setIsEditing(false);
        break;
      default:
        break;
    }
  };

  const handleEditClick = (e): void => {
    e.preventDefault();
    updateEditingState(EditorState.Editing);
  };

  const renderEditor = (
    editorData: string,
    onChange?: (data: string) => void
  ) => (
    <div className="ql-snow block w-full rounded-md border-0 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
      <Editor
        updateEditingState={(newEditState, editorData) => {
          if (onChange && newEditState === EditorState.Saved) {
            onChange(editorData ?? "");
          }
          updateEditingState(newEditState, editorData);
        }}
        editingData={editorData}
        loading={isSubmitLoading}
      />
    </div>
  );

  const renderContent = (contentData: string) => (
    <div className="ql-snow block w-full rounded-md border-0 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
      <div
        className="ql-editor"
        dangerouslySetInnerHTML={createMarkup(
          contentData ||
            (disabled
              ? `<p class="text-sm text-gray-500">${label ?? "Description"}</p>`
              : `<p class="text-sm text-gray-500">Click <span class="font-semibold">"Edit ${
                  label ?? "Description"
                }"</span> to toggle into "Edit" mode.</p>`)
        )}
      />
    </div>
  );

  return (
    <>
      <div className="flex flex-row items-center justify-between">
        {label && (
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label ?? "Description"}
          </label>
        )}
        {!disabled && (
          <button
            className="ml-4 inline-flex items-center justify-center rounded-md px-3 text-sm font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500"
            onClick={handleEditClick}>
            {isEditing ? (
              <>
                <PencilSquareIcon className="h-4 w-4" /> &nbsp;Editing
              </>
            ) : (
              <>
                <PencilIcon className="h-4 w-4" /> &nbsp;
                {`Edit ${label ?? "Description"}`}
              </>
            )}
          </button>
        )}
      </div>
      <div className="mt-2">
        {isLoading ? (
          <div className="py-6">
            <SkeletonLoader count={1} />
          </div>
        ) : isError ? (
          <div className="via-generic-error lg:py-12">
            <GenericError message={ERROR} />
          </div>
        ) : isEditing ? (
          isControlled ? (
            <Controller
              control={control}
              name={name}
              rules={rules}
              render={({ field: { value, onChange } }) =>
                renderEditor(value ?? data, onChange)
              }
            />
          ) : (
            renderEditor(data)
          )
        ) : isControlled ? (
          <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { value } }) => renderContent(value ?? data)}
          />
        ) : (
          renderContent(data)
        )}
      </div>
    </>
  );
};

export default RichTextInput;
